import { useEffect, useState } from "react";
import { AuthLayout } from "../../components/AuthLayout";
import { updatePasswordApi } from "services/auth/authApis";
import { useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import { z } from "zod";
// shadcn UI components

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

// Password validation schema
const passwordSchema = z
  .string()
  .min(8, "Password must be at least 8 characters long")
  .regex(
    /[^a-zA-Z0-9]/,
    "Password must contain at least one special character"
  );

export const UpdatePassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email;
  const token = location.state?.token;

  useEffect(() => {
    if (!email) {
      navigate("/login");
    }
  }, [email, navigate]);

  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handlePasswordChange = (value: string) => {
    setPassword(value);
    // Clear error when user starts typing
    if (error) setError("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    // Validate password
    try {
      passwordSchema.parse(password);
    } catch (err) {
      if (err instanceof z.ZodError) {
        const errorMessage = err.issues[0]?.message || "Invalid password";
        setError(errorMessage);
        toast.error(errorMessage);
        return;
      }
    }

    try {
      setLoading(true);
      const data = await updatePasswordApi(email, token, password);
      if (!data) {
        toast.error("Unable to Update Password");
      } else {
        navigate("/login");
      }
    } catch (error) {
      toast.error("Unable to Update Password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <div className="w-full">
        <div className="text-center space-y-1 mb-6">
          <p className="text-richblack-600 text-lg font-semibold mt-2">
            Update your Account password here!
          </p>
        </div>
        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="space-y-2">
            <Label htmlFor="password">New Password</Label>
            <Input
              id="password"
              onChange={(e) => handlePasswordChange(e.target.value)}
              type="password"
              placeholder="Enter new password"
              value={password}
              className={error ? "border-red-500" : ""}
              required
            />
            {error && <p className="text-red-500 text-sm mt-1">{error}</p>}

            {password && (
              <div className="mt-2 text-sm">
                <p className="font-medium mb-1">Password must contain:</p>
                <ul className="space-y-1">
                  {[
                    { check: /.{8,}/, text: "At least 8 characters" },
                    { check: /[^a-zA-Z0-9]/, text: "Special character" },
                  ].map(({ check, text }) => (
                    <li key={text} className="flex items-center">
                      <span
                        className={
                          check.test(password)
                            ? "text-green-500"
                            : "text-red-500"
                        }
                      >
                        {check.test(password) ? "✓" : "✗"}
                      </span>
                      <span className="ml-2">{text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <Button type="submit" className="w-full mt-2 hover:bg-amber-500 text-white font-semibold py-2 rounded-md transition duration-200" disabled={loading}>
            {loading ? "Updating..." : "Submit"}
          </Button>
        </form>
      </div>
    </AuthLayout>
  );
};
